
import axiosInstance from './axiosInstance';

export type CalibrationRecord = {
  calibrationId?: string;
  orgId: string;
  locationId: string;
  assetId: string;
  assetEquipmentId: string;
  vendorId: string;
  organizationName?: string;
  locationName?: string;
  vendorName?: string;
  assetName?: string;
  contactName: string;
  contactEmail: string;
  contactNumber: string;
  calibrationFrequency: 'Monthly' | 'Quarterly' | 'Yearly' | string;
  departmentId: string,
  departmentName: string,
  calibrationDate?: string;
  calibrationDueDate?: string;
  installedDate?: string;
  certificateId: string;
  priority?: 'High' | 'Medium' | 'Low' | string;
  daysToExpire?: number;
  appointmentId?: string;
  status?: string;
  createdAt?: string;
  modifiedAt?: string;
  notes?: string;
  noOfCertificates?: number;
  type: string;
};

type PaginatedCalibrationResponse = {
  data: CalibrationRecord[];
  totalCount: number;
};

export type HeaderContext = {
  orgInfo?: {
    id: string;
    name: string;
    contact: string;
    gsTin: string;
  } | null;
  locationInfo?: {
    id: string;
    name: string;
    city: string;
    state: string;
    zipcode: string;
  } | null;
  assetInfo?: {
    id: string | null;
    assetEquipmentId: string;
    name: string;
    type: string;
    status: string;
  } | null;
  vendorInfo?: {
    id: string;
    name: string;
    contactNumber: string;
    contactEmail: string;
    gsTin: string;
  } | null;
  departmentInfo?: {
    id: string;
    name: string;
  } | null;
};

export type AssetApiItem = {
  assetId: string;
  assetEquipmentId: string;
  name: string;
  type?: string;
  location: string;
  vendorId: string;
  vendorName: string;
  departmentId: string;
  departmentName: string;
  orgId: string;
  orgName: string;
  status?: string;
  installedDate?: string;
};

export type AssetListItem = { assetId: string; assetName: string };
export type VendorListItem = { vendorId: string; vendorName: string };
export type OrgListItem = { orgId: string; organizationName: string };
export type LocationListItem = { locationId: string; locationName: string };

const ASSETS_META_TTL_MS = 60_000;
const ORGS_TTL_MS = 60_000;
const LOCS_TTL_MS = 60_000;

let _assetsMetaCache:
  | {
    at: number;
    assets: AssetListItem[];
    vendors: VendorListItem[];
  }
  | null = null;

let _orgsCache:
  | {
    at: number;
    orgs: OrgListItem[];
  }
  | null = null;

let _locsCache:
  | {
    at: number;
    locations: LocationListItem[];
    byOrgId: Record<string, LocationListItem[]>;
  }
  | null = null;

async function fetchAssetsMeta() {
  const now = Date.now();
  if (_assetsMetaCache && now - _assetsMetaCache.at < ASSETS_META_TTL_MS) {
    return _assetsMetaCache;
  }

  const { data } = await axiosInstance.get<AssetApiItem[]>('/api/assets');

  const assets: AssetListItem[] = data.map((a) => ({
    assetId: a.assetId,
    assetName: a.name,
  }));

  const vendors: VendorListItem[] = Array.from(
    new Map(
      data.map(
        (a) => [a.vendorId, { vendorId: a.vendorId, vendorName: a.vendorName } as VendorListItem]
      )
    ).values()
  );

  _assetsMetaCache = { at: now, assets, vendors };
  return _assetsMetaCache;
}

async function fetchOrganizations() {
  const now = Date.now();
  if (_orgsCache && now - _orgsCache.at < ORGS_TTL_MS) return _orgsCache;

  const { data } = await axiosInstance.get<Array<{ orgId: string; orgName: string }>>(
    '/api/organizations/org id,org name'
  );

  const orgs: OrgListItem[] = data.map((o) => ({
    orgId: o.orgId,
    organizationName: o.orgName,
  }));

  _orgsCache = { at: now, orgs };
  return _orgsCache;
}

async function fetchOrgLocations() {
  const now = Date.now();
  if (_locsCache && now - _locsCache.at < LOCS_TTL_MS) return _locsCache;

  type OrgLocResponse = Array<{
    orgId: string;
    locations: Array<{ locationId: string; location: string }>;
  }>;

  const { data } = await axiosInstance.get<OrgLocResponse>(

    '/api/organizations/org id,loc id,loc name,dept id,dept name'
  );

  const byOrgId: Record<string, LocationListItem[]> = {};
  const all: LocationListItem[] = [];


  for (const entry of data) {
    const list = (entry.locations || []).map((l) => ({
      locationId: l.locationId,
      locationName: l.location,
    }));
    byOrgId[entry.orgId] = list;
    all.push(...list);
  }

  _locsCache = { at: now, locations: all, byOrgId };
  return _locsCache;
}

export type Appointment = {
  id: string;
  tenantId: string;
  orgName: string;
  name: string;
  assetId: string;
  assetName: string;
  assetEquipmentId: string;
  vendorId: string;
  vendorName: string;
  departmentId: string;
  scheduledDate: string;
  status: string;
  calibrationId: string;
  certificateId: string;
  certificatePath: string;
  createdBy: string;
  createdAt: string;
  modifiedAt: string;
};

export type CalibrationWithAppointment = {
  calibration: CalibrationRecord;
  appointment: Appointment | null;
};

export const calibrationRecordRepository = {
  getAllRecords: async (): Promise<CalibrationRecord[]> => {
    const { data } = await axiosInstance.get<CalibrationRecord[]>('/api/calibrations');
    return data;
  },

  getPaginatedRecords: async (
    pageNumber: number,
    pageSize: number,
    organization?: string,
    location?: string,
    department?: string,
    status?: string,
    search?: string,
    appliedSearch?: string
  ): Promise<PaginatedCalibrationResponse> => {
    const { data } = await axiosInstance.get('/api/calibrations', {
      params: {
        pageNumber,
        pageSize,
        organization,
        location,
        department,
        status,
        search,
        appliedSearch
      },
    });

    return data;
  },

  getRecordById: async (id: string): Promise<CalibrationRecord> => {
    const { data } = await axiosInstance.get<CalibrationRecord>(`/api/calibrations/${id}`);
    return data;
  },

  addRecord: async (record: Partial<CalibrationRecord>) => {
    const payload = { ...record };
    Object.keys(payload).forEach(
      (k) => (payload as any)[k] === undefined && delete (payload as any)[k]
    );
    await axiosInstance.post('/api/calibrations', payload);
  },

  updateRecord: async (id: string, record: Partial<CalibrationRecord>) => {
    return axiosInstance.put(`/api/calibrations/${id}`, record);
  },

  deleteRecord: async (id: string) => {
    await axiosInstance.delete(`/api/calibrations/${id}`);
  },

  getAssetsList: async (): Promise<AssetListItem[]> => {
    const { assets } = await fetchAssetsMeta();
    return assets;
  },

  getVendorsList: async (): Promise<VendorListItem[]> => {
    const { vendors } = await fetchAssetsMeta();
    return vendors;
  },

  getOrganizationsList: async (): Promise<OrgListItem[]> => {
    const { orgs } = await fetchOrganizations();
    return orgs;
  },

  getLocationsList: async (): Promise<LocationListItem[]> => {
    const { locations } = await fetchOrgLocations();
    return locations;
  },

  getLocationsByOrgId: async (orgId: string): Promise<LocationListItem[]> => {
    const { byOrgId } = await fetchOrgLocations();
    return byOrgId[orgId] || [];
  },

 exportCalibrations: async (
  format?: string,
  organization?: string,
  location?: string,
  department?: string,
  status?: string,
  search?: string,
  minDaysToExpire?: number,
  maxDaysToExpire?: number,
   pageNumber?: number,
  pageSize?: number
): Promise<Blob> => {
  const { data } = await axiosInstance.get('/api/calibrations/export', {
    responseType: 'blob',
    params: {
      format,
      organization,
      location,
      department,
      status,
      search,
      minDaysToExpire,
      maxDaysToExpire,
       pageNumber,
      pageSize,
    },
  });

  return data as Blob;
},

  getHeaderContext: async (
    orgId: string,
    locId: string,
    assetId: string,
    vendorId: string,
    departmentId: string
  ): Promise<HeaderContext> => {
    const { data } = await axiosInstance.get<HeaderContext>(
      '/api/calibrations/headercontext',
      {
        params: { orgid: orgId, locid: locId, assetid: assetId, vendorid: vendorId, departmentid: departmentId },
      }
    );
    return data;
  },

  getCalibrationsWithAppointment: async (): Promise<CalibrationWithAppointment[]> => {
    const { data } = await axiosInstance.get('/api/calibrations/with-appointment');

    return (Array.isArray(data) ? data : []).map((item: any) => ({
      calibration: { ...item.calibration, },
      appointment: item.appointment
        ? { ...item.appointment }
        : null,
    }));
  },

};

export default calibrationRecordRepository;
